export type ReviewerStatus = "pending" | "submitted" | "changes_requested" | "approved" | "rejected" | "suspended" | "active";
export interface MedicalClaims {
  profession: string;
  regulator: string;
  jurisdiction: string;
  registration_number: string;
  institution: string;
  specialties: string[];
  scope: string;
  verification_url?: string | null;
}
export interface ReviewerAccount {
  authenticated: true;
  user_id: string;
  username: string;
  name: string;
  email: string;
  organisation: string;
  role: "medical_professional" | "reviewer_applicant" | "umocare_admin";
  status: ReviewerStatus;
  claims: MedicalClaims | null;
  claims_revision: number;
  mfa_enrolled: boolean;
  case_access: boolean;
  review_notes: string;
}
export interface MfaChallenge { authenticated: false; mfa_required: true; enrollment_required: boolean; expires_at?: string }
export interface MfaEnrollment { secret: string; otpauth_uri: string; qr_svg: string }
export interface Invitation { invitation_id: string; email: string; expires_at: string; used: boolean; revoked: boolean; vetting_notes: string; issued_by: string }
export interface IssuedInvitation { invitation: Invitation; invitation_url: string; qr_svg: string }
export interface AccountDetail { account: ReviewerAccount; audit: Record<string, unknown>[] }

export const emptyClaims = (): MedicalClaims => ({ profession: "", regulator: "", jurisdiction: "Malawi", registration_number: "", institution: "", specialties: [], scope: "", verification_url: null });

export class ReviewerApiError extends Error {
  constructor(readonly status: number, message: string, readonly code: string | null = null) { super(message); this.name = "ReviewerApiError"; }
}

async function safeErrorCode(response: Response): Promise<string | null> {
  const reader = response.body?.getReader();
  if (!reader) return null;
  try {
    const decoder = new TextDecoder(); let bytes = 0; let text = "";
    while (true) {
      const chunk = await reader.read(); if (chunk.done) break;
      bytes += chunk.value.byteLength;
      if (bytes > 4096) return null;
      text += decoder.decode(chunk.value, {stream: true});
    }
    const value: unknown = JSON.parse(text + decoder.decode());
    if (typeof value !== "object" || value === null) return null;
    const envelope = value as Record<string, unknown>;
    const error = typeof envelope.error === "object" && envelope.error !== null ? envelope.error as Record<string, unknown> : envelope;
    return typeof error.code === "string" && error.code.length <= 80 ? error.code : null;
  } catch {return null;}
  finally {await reader.cancel().catch(() => undefined); reader.releaseLock();}
}

export async function reviewerRequest<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const controller = new AbortController();
  const abort = () => controller.abort(signal?.reason);
  if (signal?.aborted) abort();
  signal?.addEventListener("abort", abort, {once: true});
  let onAbort = () => {};
  const cancelled = new Promise<T>((_resolve, reject) => {
    onAbort = () => reject(controller.signal.reason instanceof Error ? controller.signal.reason : new DOMException("The request was cancelled.", "AbortError"));
    controller.signal.addEventListener("abort", onAbort, {once: true});
  });
  const timer = setTimeout(() => controller.abort(new Error("The request timed out. Try again.")), 30_000);
  try {
    controller.signal.throwIfAborted();
    return await Promise.race([cancelled, (async () => {
    const response = await fetch(`/api/ui/professional${path}`, {method: body === undefined ? "GET" : "POST", credentials: "same-origin", cache: "no-store", headers: {"Content-Type": "application/json"}, ...(body === undefined ? {} : {body: JSON.stringify(body)}), signal: controller.signal});
    if (!response.ok) {
      const code = await safeErrorCode(response);
      const messages: Record<string, string> = {MFA_CODE_INVALID: "That code was not accepted. Check the current authenticator code or try an unused recovery code.", MFA_CHALLENGE_REQUIRED: "This sign-in step has expired. Start a new password sign-in.", AUTHENTICATION_FAILED: "Sign-in was not accepted. Check your username or work email and password.", CLAIMS_INVALID: "Check all professional credential fields, your clinical scope and any registration link before submitting again.", CLAIMS_REVISION_CONFLICT: "These credentials changed. Refresh the application before submitting again.", INVITATION_EMAIL_MISMATCH: "This invitation is tied to a different email. Ask your UmoCare inviter to check the intended recipient.", PASSWORD_INVALID: "Use a password with at least 12 characters.", IDENTITY_DETAILS_INVALID: "Check your full name, username and individual work email."};
      const message = (code ? messages[code] : undefined) ?? (response.status === 409 ? "This account changed. Refresh it before submitting your review again." : response.status === 401 ? "Your sign-in has ended. Sign in again to continue." : response.status === 403 ? "Your account does not have access to this action." : response.status === 410 ? "This invitation or sign-in step has expired. Ask UmoCare staff for a new invitation, or sign in again." : "This step could not be completed. Check your details and try again.");
      throw new ReviewerApiError(response.status, message, code);
    }
    const result = await response.json() as T;
    controller.signal.throwIfAborted();
    return result;
    })()]);
  } finally { clearTimeout(timer); signal?.removeEventListener("abort", abort); controller.signal.removeEventListener("abort", onAbort); }
}

export function readInviteFragment(fragment: string): string | null {
  const value = new URLSearchParams(fragment.replace(/^#/, "")).get("invite");
  return value && value.length <= 4096 ? value : null;
}
